const _mockReducers = {};
const _mockActions = {};

/**
 * Checks to see if an action matches this handler
 * @name reducerMatcher
 * @function
 * @param {Object} action - The action object to match against
 */
/**
 * Processes the action and produces an output
 * @name reducerProcessor
 * @function
 * @param {Object} action - The action object to process
 * @param {*}      state - The current state, before reducers are applied
 * @param {*}      defaultState - The default state value
 */
/**
 * Add a mock reducer to the list of reducers in this service
 *
 * @param  {Object}           reducer - The mock reducer definition
 * @param  {Object}           reducer.defaultState - The default store state to return when no reducer handlers match
 * @param  {Object[]}         reducer.handlers - An array of reducer handlers to match actions against
 * @param  {reducerMatcher=}  reducer.handlers[].matches - Checks to see if an action matches this handler
 * @param  {reducerProcessor} reducer.handlers[].outputs - Processes the action and produces an output
 * @param  {string=}          reducer.handlers[].type - The type of action that matches this handler (required if `matches` is absent)
 * @param  {string}           reducer.name - The name of this reducer, which will be its key in the _mockReducers object
 */
export const addMockReducer = ({ defaultState, handlers, name }) => {
  _mockReducers[name] = (state, action) => {
    for (const reducer of handlers) {
      const { matches, outputs, type } = reducer;
      const isMatching = matches || ((action) => action.type === type);
      if (isMatching(action)) {
        return outputs(action, state, defaultState);
      }
    }
    return defaultState;
  };
};

/**
 * Processes the input data for the action
 * @name actionProcessor
 * @function
 * @param {...*} input - The list of input arguments to process
 */
/**
 * Add a mock action to the list of actions in this service
 *
 * @param  {Object}           action - The mock action definition
 * @param  {actionProcessor}  action.action - Processes the input data for the action
 * @param  {string}           action.name - The name of this action, which will be its key in the _mockActions object
 * @param  {string}           action.type - The type of action to dispatch
 */
export const addMockAction = ({ action, name, type }) => {
  _mockActions[name] =
    (...input) =>
    async (dispatch) => {
      const data = action(...input);
      dispatch({ payload: { ...data }, type });
    };
};

/**
 * Get the full list of reducers that have been added to this service
 *
 * @return {any}           Returns the full list of reducers currently in this service
 */
export const getMockReducers = () => _mockReducers;

/**
 * The redux store dispatcher, to apply to the actions being outputted
 * @name dispatcher
 * @function
 */
/**
 * Get the full list of actions that have been added to this service
 *
 * @param  {dispatcher=} dispatch - The redux store dispatcher, to apply to the actions being outputted
 * @return {any}      Returns the full list of actions currently in this service
 */
export const getMockActions = (dispatch) => {
  if (!dispatch) {
    return _mockActions;
  }
  const actions = {};
  Object.keys(_mockActions).forEach((name) => {
    const action = _mockActions[name];
    actions[name] = (...input) => action(...input)(dispatch);
  });
  return actions;
};
